<script setup lang="ts">
import { TrendingUp, ArrowRight, Beaker } from 'lucide-vue-next';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { slugify } from '@/lib/utils';
import { useRouter } from 'vue-router';

const props = defineProps<{
    inv: any;
    isAmountsHidden: boolean;
    formatAmount: (val: any) => string;
}>();

const router = useRouter();

const goToDetail = () => {
    router.push({ 
        name: 'finance_investment_detail', 
        params: { id: props.inv.id, name: slugify(props.inv.name) } 
    });
};
</script>

<template>
    <Card 
      class="group cursor-pointer border-border shadow-sm hover:border-primary hover:shadow-md transition-all duration-300 flex flex-col overflow-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none" 
      @click="goToDetail"
      @keyup.enter="goToDetail"
      @keyup.space="goToDetail"
      role="listitem"
      tabindex="0"
      :aria-label="`Détail de l'investissement : ${inv.name}. Valorisation actuelle : ${formatAmount(inv.currentValue)}€`"
    >
      <CardHeader class="pb-2">
        <div class="flex justify-between items-start gap-2">
          <CardTitle class="text-lg font-bold group-hover:text-primary transition-colors truncate">{{ inv.name }}</CardTitle>
          <div class="flex flex-wrap justify-end gap-1.5 shink-0">
            <Badge v-if="inv.category" variant="outline" class="bg-zinc-50 border-zinc-100 text-zinc-500 font-bold text-[10px]">
              {{ inv.category.name }}
            </Badge>
          </div>
        </div>
        <div class="flex gap-2">
          <span v-if="inv.ignoreInGlobalSums && !inv.isSimulation" class="text-[9px] font-bold bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded border border-amber-100 uppercase tracking-tighter">Exclu Global</span>
          <span v-if="inv.isSimulation" class="text-[9px] font-bold bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded border border-purple-200 uppercase tracking-tighter shadow-sm flex items-center gap-1"><Beaker class="w-2.5 h-2.5" /> Simulation</span>

          <span v-if="inv.ignorePnL" class="text-[9px] font-bold bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded border border-zinc-200 uppercase tracking-tighter">P&L Masqué</span>
        </div>
      </CardHeader>
      
      <CardContent class="flex-1 space-y-4">
        <div class="flex justify-between items-center bg-muted/30 p-3 rounded-lg border border-border group-hover:bg-muted/50 transition-colors">
          <div class="flex flex-col">
            <span class="text-[10px] font-black uppercase tracking-widest text-zinc-400">Valorisation</span>
            <span class="text-xl font-black text-emerald-600 tabular-nums" :class="{ 'blur-md select-none': isAmountsHidden }">
              {{ formatAmount(inv.currentValue) }}€
            </span>
          </div>
          <div class="flex flex-col items-end">
            <span class="text-[10px] font-black uppercase tracking-widest text-zinc-400">Plus-value</span>
            <span class="text-lg font-black tabular-nums" 
                  :class="[(inv.currentValue - inv.investedAmount) >= 0 ? 'text-emerald-500' : 'text-red-500', { 'blur-md select-none': isAmountsHidden }]">
              {{ (inv.currentValue - inv.investedAmount) >= 0 ? '+' : '' }}{{ formatAmount(Math.abs(inv.currentValue - inv.investedAmount)) }}€
            </span>
          </div>
        </div>
        
        <p class="text-sm text-muted-foreground leading-relaxed italic line-clamp-2 min-h-[2.5rem]">
          {{ inv.description || 'Aucune description fournie' }}
        </p>
      </CardContent>

      <CardFooter class="bg-muted/40 border-t border-border pt-4 flex justify-between items-center group-hover:bg-muted/60 transition-colors">
        <div v-if="inv.expectedReturnRate" class="flex items-center gap-1.5 text-xs font-bold text-emerald-500">
          <TrendingUp class="w-3.5 h-3.5" /> {{ parseFloat(inv.expectedReturnRate).toFixed(2) }}% / an
        </div>
        <div v-else class="text-xs text-muted-foreground italic">Pas de rendement défini</div>
        <div class="text-xs font-bold text-foreground flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
          Gérer <ArrowRight class="w-3 h-3" />
        </div>
      </CardFooter>
    </Card>
</template>
